import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HubInfoUrlProvider } from './../../api';
import { isNotNil, isString, parseURL, wchGetBaseURL, wchGetSiteURL, wchUrlFromProvider } from './../../utils';
import { createBaseURL } from './url.utils';
import { BFF_BASE_URL } from './urls';

const LOGGER = 'WchUtils';

export const KEY_ON_RENDERING_CONTEXT = 'onRenderingContext';
export const KEY_ON_LAYOUT_MODE = 'onLayoutMode';

/**
 * Decodes the path prefix from the URL that is the starting segment
 *
 * @param aUrl the potentially absolute URL
 * @return the path prefix
 */
function _getPrefix(aUrl: string): string {
  // parse the URL
  const url = parseURL(aUrl);
  return isNotNil(url) ? url.pathname : aUrl;
}

/**
 * Decodes the base href of the application from the config or the doc fallback
 *
 * @param aBaseUrl the optional base URL
 * @param aDoc the document
 *
 * @return the path prefix that is supposed to be recognized as URLs
 */
function _getAppBaseURL(
  aBaseUrl?: HubInfoUrlProvider,
  aDoc?: Document
): string {
  // test if we can decode the URL from the config
  const fromConfig = createBaseURL(wchUrlFromProvider(aBaseUrl));
  // otherwise fallback
  return isString(fromConfig) ? fromConfig : createBaseURL(wchGetBaseURL(aDoc));
}

/**
 * Decodes the base href of the application from the config or the doc fallback
 *
 * @param aBaseUrl the optional base URL
 * @param aDoc the document
 *
 * @return the path prefix that is supposed to be recognized as URLs
 */
function _getAppBaseHref(
  aBaseUrl?: HubInfoUrlProvider,
  aDoc?: Document
): string {
  // test if we can decode the URL from the config
  return _getPrefix(_getAppBaseURL(aBaseUrl, aDoc));
}

/*
 * Returns the URI path for the rendering context
 *
 * @param aID   the ID
 * @return the path
 */
function _getRenderingContextURL(aID: string): Observable<string> {
  return BFF_BASE_URL.pipe(
    map(baseUrl => `${baseUrl}context/${encodeURIComponent(aID)}`)
  );
}

/*
 * Returns the URI path for the site
 *
 * @return the path
 */
function _getSiteURL(): Observable<string> {
  // replace
  return BFF_BASE_URL.pipe(map(wchGetSiteURL));
}

export {
  _getAppBaseURL as wchGetAppBaseURL,
  _getAppBaseHref as wchGetAppBaseHref,
  _getSiteURL as wchGetSiteURL,
  _getRenderingContextURL as wchGetRenderingContextURL
};
